import { useState, useEffect, useCallback } from 'react';
import { Idol, Stat } from 'types';
import { ITEM_TYPES } from 'constants/index';
import { UNIQUE_IDOLS } from '../constants/uniqueIdols';
import { modDatabase } from 'data/mods';
import { 
  IdolModifier, 
  FixedModifier,
  FixedValueModifier,
  VariableModifier 
} from '../types';

export const useUniqueIdols = () => {
  const [selectedUniqueIdol, setSelectedUniqueIdol] = useState<string>('');
  // This will store all modifier types
  const [idolModifiers, setIdolModifiers] = useState<IdolModifier[]>([]);
  // This tracks which variable modifiers are selected for idols like Twisted Wish
  const [selectedVariableModifiers, setSelectedVariableModifiers] = useState<number[]>([]);

  // Handle unique idol selection with memoized dependencies
  useEffect(() => {
    if (!selectedUniqueIdol) {
      // Clear modifiers when no idol is selected
      setIdolModifiers([]);
      setSelectedVariableModifiers([]);
      return;
    }

    const selectedIdol = UNIQUE_IDOLS.find(idol => idol.id === selectedUniqueIdol);
    if (!selectedIdol) return;

    const modifiers: IdolModifier[] = [];

    // Process fixed modifiers (non-adjustable)
    if (selectedIdol.fixedModifiers) {
      selectedIdol.fixedModifiers.forEach(mod => {
        const fixedMod: FixedModifier = {
          modId: mod.modId,
          defaultValue: mod.defaultValue,
          isFixed: true,
          isAdjustable: false
        };
        modifiers.push(fixedMod);
      });
    }

    // Process fixed modifiers with adjustable values
    if (selectedIdol.fixedValueModifiers) {
      selectedIdol.fixedValueModifiers.forEach(mod => {
        const adjustableMod: FixedValueModifier = {
          modId: mod.modId,
          defaultValue: mod.defaultValue,
          range: mod.range,
          isFixed: true,
          isAdjustable: true,
          value: mod.defaultValue
        };
        modifiers.push(adjustableMod);
      });
    }

    // For special idols with variable modifiers, we don't add them yet
    // They will be added when user selects them

    setIdolModifiers(modifiers);
  }, [selectedUniqueIdol]); // Only depend on selectedUniqueIdol

  // Update modifier value (works for both fixed-value and variable modifiers)
  const updateModifierValue = useCallback((modId: number, value: number) => {
    setIdolModifiers(prevModifiers => {
      // Check if this modifier already exists in the list
      const modifierExists = prevModifiers.some(mod => mod.modId === modId);
      
      if (modifierExists) {
        // Update existing modifier
        return prevModifiers.map(mod => {
          if (mod.modId === modId && mod.isAdjustable) {
            if (mod.isFixed) {
              // It's a FixedValueModifier
              return { ...mod, value } as FixedValueModifier;
            } else {
              // It's a VariableModifier
              return { ...mod, value } as VariableModifier;
            }
          }
          return mod;
        });
      } else {
        // Find the selected idol to get modifier definitions
        const selectedIdol = UNIQUE_IDOLS.find(idol => idol.id === selectedUniqueIdol);
        if (!selectedIdol) return prevModifiers;
        
        // Try to find the modifier definition
        let modDefinition;
        
        // Check in variableModifiers
        if (selectedIdol.variableModifiers) {
          modDefinition = selectedIdol.variableModifiers.find(m => m.modId === modId);
        }
        
        // Check in fixedValueModifiers if not found in variableModifiers
        if (!modDefinition && selectedIdol.fixedValueModifiers) {
          modDefinition = selectedIdol.fixedValueModifiers.find(m => m.modId === modId);
        }
        
        // If we found a definition, create a new modifier
        if (modDefinition) {
          const newMod: VariableModifier = {
            modId,
            defaultValue: modDefinition.defaultValue,
            range: modDefinition.range,
            isFixed: false,
            isAdjustable: true,
            value
          };
          
          return [...prevModifiers, newMod];
        }
        
        // If no definition found, just return the previous state
        return prevModifiers;
      }
    });
  }, [selectedUniqueIdol]);

  // Add a variable modifier (for things like Twisted Wish)
  const addVariableModifier = useCallback((modId: number) => {
    const selectedIdol = UNIQUE_IDOLS.find(idol => idol.id === selectedUniqueIdol);
    if (!selectedIdol || !selectedIdol.variableModifiers) return;

    const modDefinition = selectedIdol.variableModifiers.find(mod => mod.modId === modId);
    if (!modDefinition) return;

    // Create the new variable modifier
    const newModifier: VariableModifier = {
      modId,
      defaultValue: modDefinition.defaultValue,
      range: modDefinition.range,
      isFixed: false,
      isAdjustable: true,
      value: modDefinition.defaultValue
    };

    // Add to the list of modifiers
    setIdolModifiers(prev => [...prev, newModifier]);
    
    // Track it in selected variable modifiers
    setSelectedVariableModifiers(prev => [...prev, modId]);
  }, [selectedUniqueIdol]);

  // Replace a variable modifier (useful for Twisted Wish slot-based selection)
  const replaceVariableModifier = useCallback((index: number, modId: number) => {
    const selectedIdol = UNIQUE_IDOLS.find(idol => idol.id === selectedUniqueIdol);
    if (!selectedIdol || !selectedIdol.variableModifiers) return;

    // If modId is empty (0 or ''), clear the slot
    if (!modId) {
      // Remove the old modifier from this slot if it exists
      const oldModId = selectedVariableModifiers[index];
      if (oldModId) {
        // Remove old modifier from idolModifiers
        setIdolModifiers(prev => prev.filter(mod => mod.modId !== oldModId || mod.isFixed));
        
        // Clear this slot in selectedVariableModifiers
        setSelectedVariableModifiers(prev => {
          const updated = [...prev];
          updated[index] = 0; // Set to 0 to mark empty
          return updated;
        });
      }
      return;
    }

    const modDefinition = selectedIdol.variableModifiers.find(mod => mod.modId === modId);
    if (!modDefinition) return;

    // If there's an existing modifier at this index, remove it
    const oldModId = selectedVariableModifiers[index];
    if (oldModId) {
      // Remove old modifier from idolModifiers
      setIdolModifiers(prev => prev.filter(mod => mod.modId !== oldModId || mod.isFixed));
    }

    // Find if this modifier already exists in idolModifiers
    const existingModifier = idolModifiers.find(mod => mod.modId === modId);
    
    if (!existingModifier) {
      // Create the new variable modifier
      const newModifier: VariableModifier = {
        modId,
        defaultValue: modDefinition.defaultValue,
        range: modDefinition.range,
        isFixed: false,
        isAdjustable: true,
        value: modDefinition.defaultValue
      };

      // Add to the list of modifiers
      setIdolModifiers(prev => [...prev, newModifier]);
    }
    
    // Update selected variables
    setSelectedVariableModifiers(prev => {
      const updated = [...prev];
      updated[index] = modId;
      return updated;
    });
  }, [selectedUniqueIdol, selectedVariableModifiers, idolModifiers]);

  // Check if unique idol form is valid
  const isUniqueFormValid = useCallback(() => {
    const selectedIdol = UNIQUE_IDOLS.find(idol => idol.id === selectedUniqueIdol);
    if (!selectedIdol) return false;
    
    // For special idols like Twisted Wish with variable requirements
    if (selectedIdol.isSpecial && selectedIdol.variableModifiersRequired) {
      return selectedVariableModifiers.filter(id => id > 0).length === selectedIdol.variableModifiersRequired;
    }
    
    // For standard unique idols, just having a selection is enough
    return true;
  }, [selectedUniqueIdol, selectedVariableModifiers]);

  // Create the unique idol with all modifiers
  const createUniqueIdol = useCallback((idolName: string) => {
    if (!selectedUniqueIdol) {
      throw new Error("Please select a unique idol.");
    }
    
    const selectedIdol = UNIQUE_IDOLS.find(idol => idol.id === selectedUniqueIdol);
    if (!selectedIdol) {
      throw new Error("Invalid unique idol selected.");
    }

    // Perform validations for special idols
    if (selectedIdol.isSpecial && selectedIdol.variableModifiersRequired) {
      const selectedCount = selectedVariableModifiers.filter(id => id > 0).length;
      if (selectedCount !== selectedIdol.variableModifiersRequired) {
        throw new Error(
          `Please select exactly ${selectedIdol.variableModifiersRequired} variable modifiers.`
        );
      }
    }
    
    // Create stats array from all fixed modifiers
    const stats: Stat[] = [];
    
    // Add all fixed modifiers
    idolModifiers
      .filter(mod => mod.isFixed)
      .forEach(modifier => {
        const modInfo = modDatabase.mods[modifier.modId];
        
        // For adjustable modifiers, use their current value
        // For fixed modifiers, use the defaultValue
        let value = 0;
        
        if (modifier.isAdjustable) {
          // Handle adjustable modifiers with proper type checking
          const adjustableMod = modifier as FixedValueModifier;
          value = adjustableMod.value !== undefined ? adjustableMod.value : (adjustableMod.defaultValue || 0);
        } else {
          // Handle non-adjustable fixed modifiers
          const fixedMod = modifier as FixedModifier;
          value = fixedMod.defaultValue || 0;
        }
        
        stats.push({
          explicitType: "prefix", // Unique idols only have prefixes
          value,
          contentType: modInfo?.contentType || "",
          modId: modifier.modId,
        });
      });
      
    // Add variable modifiers for special idols like Twisted Wish
    if (selectedIdol.isSpecial && selectedIdol.variableModifiers) {
      // Only include the selected variable modifiers
      selectedVariableModifiers
        .filter(modId => modId > 0) // Filter out empty slots (0 or undefined)
        .forEach(modId => {
          // Find this modifier in idolModifiers or create a default one
          const modifier = idolModifiers.find(mod => mod.modId === modId);
          const modInfo = modDatabase.mods[modId];
          
          // Get the value either from the modifier or find definition in variableModifiers
          let value: number;
          if (modifier && 'value' in modifier) {
            value = modifier.value;
          } else {
            // Find in variableModifiers
            const modDef = selectedIdol.variableModifiers?.find(m => m.modId === modId);
            value = modDef?.defaultValue || 85; // Fallback to 85 if not found
          }
          
          stats.push({
            explicitType: "prefix",
            value,
            contentType: modInfo?.contentType || "",
            modId,
          });
        });
    } else {
      // For regular unique idols, add variable modifiers
      idolModifiers
        .filter(mod => !mod.isFixed)
        .forEach(modifier => {
          const modInfo = modDatabase.mods[modifier.modId];
          
          // Use proper type assertion
          const variableMod = modifier as VariableModifier;
          const value = variableMod.value !== undefined ? variableMod.value : (variableMod.defaultValue || 0);
          
          stats.push({
            explicitType: "prefix",
            value,
            contentType: modInfo?.contentType || "",
            modId: modifier.modId,
          });
        });
    }
    
    const newIdol: Idol = {
      id: `unique-${selectedIdol.id}-${Date.now()}`,
      size: ITEM_TYPES.UNIQUE,
      name: idolName || selectedIdol.name,
      stats,
    };
    
    return newIdol;
  }, [selectedUniqueIdol, selectedVariableModifiers, idolModifiers]);

  // Check if a variable modifier is selected
  const isVariableModifierSelected = useCallback((modId: number) => {
    return selectedVariableModifiers.includes(modId);
  }, [selectedVariableModifiers]);

  // Reset form state
  const resetUniqueIdolForm = useCallback(() => {
    setSelectedUniqueIdol('');
    setIdolModifiers([]);
    setSelectedVariableModifiers([]);
  }, []);

  return {
    // State
    selectedUniqueIdol,
    setSelectedUniqueIdol,
    idolModifiers,
    setIdolModifiers,
    selectedVariableModifiers,
    setSelectedVariableModifiers,
    
    // Methods
    updateModifierValue,
    addVariableModifier,
    replaceVariableModifier,
    createUniqueIdol,
    isVariableModifierSelected,
    isUniqueFormValid,
    resetUniqueIdolForm,
    
    // Constant
    UNIQUE_IDOLS,
  };
};

export default useUniqueIdols;